import { SolanaAddress } from "@solomon-apps/shared/src/utils/address/solana";

import { preflightMultisig } from "@/services/clientPositions";

import type { MultisigSummary } from "./steps";

/** The default destination's name for a Squads source: rewards go to the
 *  multisig's vault, never its account. */
export const MULTISIG_VAULT_LABEL = "Multisig Vault";

/**
 * The destination to use for `address` given the Squads source: the source's
 * own multisig account resolves to the default (`""`, its vault), so the user
 * never has to know which of the two addresses they pasted. Anything else is
 * kept as typed.
 */
export function resolveSourceMultisigDestination(address: string, multisig: MultisigSummary | null): string {
    const trimmed = address.trim();
    if (multisig !== null && trimmed === multisig.address) return "";
    return address;
}

/**
 * The address to save for a typed-in destination: a Squads multisig account
 * (any multisig, not only the source's) becomes its vault, because rewards
 * sent to the account are lost. The backend preflight answers that from the
 * account itself, so it needs no directory scan. A wallet (on the curve, so
 * never a multisig account) skips the lookup; a vault or a failed lookup keeps
 * the address as typed.
 */
export async function resolveMultisigAccountToVault(address: string): Promise<string> {
    if (SolanaAddress.isOnCurve(address)) return address;
    try {
        const response = await preflightMultisig({ address, signerAddress: null });
        if (response.status === "ok" && response.resolvedFrom === "multisig" && response.vaultAddress) {
            return response.vaultAddress;
        }
    } catch {
        // Lookup unavailable: keep the address as typed.
    }
    return address;
}
